<script setup lang="ts">
import axios from 'axios'
import { notifyError } from '@/utils/helpers'

definePage({
    meta: {
        id: 'capteurs.appareils',
        title: 'Appareils IoT',
        icon: 'mdi-devices',
        drawerIndex: 6,
        requiresAuth: true,
        requiresAdmin: true

    },
})

const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    deviceProfileId: { id: string; entityType: string }
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

declare interface TbPageData<T> {
    data: T[]
    totalPages: number
    totalElements: number
    hasNext: boolean
}

declare interface TreeNode {
    id: string
    title: string
    children?: TreeDevice[]
    isType?: boolean
}

declare interface TreeDevice {
    id: string
    title: string
    label?: string
    deviceId: string
    type: string
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

// ── State ─────────────────────────────────────────────────────────────────────

const allDevices = ref<TbDevice[]>([])
const treeItems = ref<TreeNode[]>([])
const treeOpened = ref<string[]>([])
const treeActivated = ref<string[]>([])
const deviceSearch = ref('')

const selectedDevice = ref<TbDevice | null>(null)

// ── Computed tree items with search filter ────────────────────────────────────

const filteredTreeItems = computed<TreeNode[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return treeItems.value

    return treeItems.value
        .map(group => ({
            ...group,
            children: group.children?.filter(d =>
                d.title.toLowerCase().includes(q) ||
                (d.label ?? '').toLowerCase().includes(q)
            ),
        }))
        .filter(group => (group.children?.length ?? 0) > 0)
})

// ── Fetch all devices (paginated) ─────────────────────────────────────────────

async function fetchAllDevices(token: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[] = []

    while (true) {
        const url = `${TB_BASE_URL}/api/tenant/devices?pageSize=${PAGE_SIZE}&page=${page}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }

    return result
}

// ── Build tree grouped by device type ────────────────────────────────────────

function buildTree(devices: TbDevice[]): TreeNode[] {
    const groups = new Map<string, TbDevice[]>()

    for (const device of devices) {
        const type = device.type || 'Sans type'
        if (!groups.has(type)) groups.set(type, [])
        groups.get(type)!.push(device)
    }

    const tree: TreeNode[] = []
    for (const [type, devs] of [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        tree.push({
            id: `type:${type}`,
            title: `${type} (${devs.length})`,
            isType: true,
            children: devs
                .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
                .map(d => ({
                    id: `device:${d.id.id}`,
                    title: d.label || d.name,
                    label: d.label ? d.name : undefined,
                    deviceId: d.id.id,
                    type: d.type,
                    createdTime: d.createdTime,
                    additionalInfo: d.additionalInfo,
                })),
        })
    }
    return tree
}

// ── Selection ─────────────────────────────────────────────────────────────────

function onActivate(activated: string[]) {
    treeActivated.value = activated
    const nodeId = activated[0]
    if (!nodeId?.startsWith('device:')) {
        selectedDevice.value = null
        return
    }
    const deviceId = nodeId.replace('device:', '')
    selectedDevice.value = allDevices.value.find(d => d.id.id === deviceId) ?? null
}

// ── Load data ─────────────────────────────────────────────────────────────────

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    selectedDevice.value = null
    treeActivated.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const devices = await fetchAllDevices(token)
        allDevices.value = devices
        treeItems.value = buildTree(devices)
        treeOpened.value = treeItems.value.map(g => g.id)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString('fr-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 85vh; display: flex; flex-direction: column;">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-devices" size="26" />
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Appareils IoT</div>
                        <div class="text-caption text-medium-emphasis">
                            {{ allDevices.length }} appareil{{ allDevices.length !== 1 ? 's' : '' }} ThingsBoard
                        </div>
                    </div>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn color="primary" variant="tonal" rounded="pill" prepend-icon="mdi-refresh"
                        :loading="isLoading" @click="loadDevices">
                        Rafraîchir
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Login error banner -->
        <v-alert v-if="loginError" type="error" variant="tonal" density="compact" class="mx-4 mt-3 mb-0"
            rounded="lg">
            {{ loginError }}
        </v-alert>

        <!-- Main content: tree + detail panel -->
        <div class="d-flex" style="flex: 1 1 auto; overflow: hidden;">

            <!-- Left: device tree -->
            <div class="tree-panel d-flex flex-column"
                :style="{ width: $vuetify.display.smAndDown ? '100%' : '300px', borderRight: '1px solid rgba(var(--v-border-color), var(--v-border-opacity))' }">

                <!-- Search -->
                <div class="pa-3">
                    <v-text-field v-model="deviceSearch" label="Rechercher un appareil" density="compact"
                        variant="outlined" rounded="lg" hide-details clearable prepend-inner-icon="mdi-magnify" />
                </div>

                <v-divider />

                <!-- Tree view -->
                <div style="flex: 1 1 auto; overflow-y: auto;">
                    <v-list density="compact" nav open-strategy="multiple" :opened="treeOpened"
                        @update:opened="treeOpened = $event">
                        <v-list-group v-for="group in filteredTreeItems" :key="group.id" :value="group.id">
                            <template #activator="{ props }">
                                <v-list-item v-bind="props" density="compact" rounded="lg">
                                    <template #prepend>
                                        <v-icon icon="mdi-folder-outline" size="18" color="primary" class="mr-2" />
                                    </template>
                                    <v-list-item-title class="text-caption font-weight-medium">
                                        {{ group.title }}
                                    </v-list-item-title>
                                </v-list-item>
                            </template>

                            <v-list-item
                                v-for="device in group.children"
                                :key="device.id"
                                density="compact"
                                rounded="lg"
                                :active="treeActivated[0] === device.id"
                                color="primary"
                                @click="onActivate([device.id])"
                            >
                                <template #prepend>
                                    <v-icon icon="mdi-chip" size="16" color="secondary" class="mr-2" />
                                </template>
                                <v-list-item-title class="text-caption">{{ device.title }}</v-list-item-title>
                                <v-list-item-subtitle v-if="device.label" class="text-caption">
                                    {{ device.label }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list-group>
                    </v-list>

                    <div v-if="!isLoading && filteredTreeItems.length === 0 && !loginError"
                        class="pa-4 text-center text-medium-emphasis text-caption">
                        Aucun appareil trouvé
                    </div>
                </div>
            </div>

            <!-- Right: device detail — hidden on mobile when tree is shown -->
            <div v-if="!$vuetify.display.smAndDown || selectedDevice"
                style="flex: 1 1 auto; overflow-y: auto; padding: 16px;">

                <!-- Placeholder when nothing selected -->
                <div v-if="!selectedDevice"
                    class="d-flex flex-column align-center justify-center text-medium-emphasis"
                    style="height: 100%; gap: 12px;">
                    <v-icon icon="mdi-gesture-tap" size="48" />
                    <span class="text-body-2">Sélectionnez un appareil dans l'arbre</span>
                </div>

                <!-- Device detail card -->
                <template v-else>
                    <!-- Back button on mobile -->
                    <v-btn v-if="$vuetify.display.smAndDown" variant="text" prepend-icon="mdi-arrow-left"
                        class="mb-3" @click="selectedDevice = null; treeActivated = []">
                        Retour
                    </v-btn>

                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center" style="gap: 10px;">
                            <v-avatar color="secondary" variant="tonal" size="36" rounded="lg">
                                <v-icon icon="mdi-chip" size="20" />
                            </v-avatar>
                            <div>
                                <div class="text-subtitle-1 font-weight-bold">
                                    {{ selectedDevice.label || selectedDevice.name }}
                                </div>
                                <div v-if="selectedDevice.label" class="text-caption text-medium-emphasis">
                                    {{ selectedDevice.name }}
                                </div>
                            </div>
                        </v-card-title>

                        <v-divider />

                        <v-card-text>
                            <v-list density="compact" lines="two">
                                <v-list-item prepend-icon="mdi-tag-outline" title="Type">
                                    <template #subtitle>
                                        <v-chip size="x-small" color="primary" variant="tonal" class="mt-1">
                                            {{ selectedDevice.type || '—' }}
                                        </v-chip>
                                    </template>
                                </v-list-item>

                                <v-list-item prepend-icon="mdi-identifier" title="Identifiant"
                                    :subtitle="selectedDevice.id.id" />

                                <v-list-item prepend-icon="mdi-calendar-outline" title="Date de création"
                                    :subtitle="formatDate(selectedDevice.createdTime)" />
                            </v-list>

                            <!-- Additional info -->
                            <template
                                v-if="selectedDevice.additionalInfo && Object.keys(selectedDevice.additionalInfo).length > 0">
                                <v-divider class="my-3" />
                                <div class="text-caption text-medium-emphasis mb-2">Informations additionnelles</div>
                                <v-card variant="tonal" rounded="lg" class="pa-3">
                                    <pre class="text-caption"
                                        style="white-space: pre-wrap; word-break: break-all;">{{ JSON.stringify(selectedDevice.additionalInfo, null, 2) }}</pre>
                                </v-card>
                            </template>
                        </v-card-text>
                    </v-card>
                </template>
            </div>
        </div>
    </v-card>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Tree panel ──────────────────────────── */
.tree-panel {
    flex-shrink: 0;
}
</style>
